import { useState, useEffect, useMemo } from 'react';
import { Table } from 'antd';
import { SERVICES_PRODUCT_ID } from 'components/common/constant';
import dayjs from 'dayjs';
import {
  useGetServicesReport,
  useGetServicesReportNoPagination
} from 'hooks/uesGetConsolidateRevenue';
import { useTranslation } from 'react-i18next';
import { getColumnServicesReport } from 'constants/table';
import { PAGE_SIZE_CONSOLIDATE_SOURCE_DETAILS } from 'services/api/module/report.api';
import { formatCurrency } from 'utils';

interface ServiceConsolidateRevenueProps {
  filterOptions: any;
  isEnable: boolean;
  setDataSource?: (data: any[]) => void;
  branch_ids?: string;
}

export default function ServiceTab({
  filterOptions,
  isEnable,
  setDataSource,
  branch_ids
}: ServiceConsolidateRevenueProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_CONSOLIDATE_SOURCE_DETAILS);

  const startDate = filterOptions.start_date;
  const endDate = dayjs(filterOptions.start_date)
    .add(filterOptions.period - 1, 'day')
    .format('YYYY-MM-DD');

  const filterParams = {
    start_date: startDate,
    end_date: endDate,
    product_id: filterOptions.product_id
      ? filterOptions.product_id
      : SERVICES_PRODUCT_ID.transportation,
    ...(branch_ids && { branch_ids })
  };

  const {
    data: servicesReport,
    pagination,
    isLoading
  } = useGetServicesReport(filterParams, page, pageSize, isEnable);

  const { data: servicesReportNoPagination } = useGetServicesReportNoPagination(
    filterParams,
    isEnable
  );

  const columns = getColumnServicesReport(t);

  // Calculate totals
  const totals = useMemo(() => {
    if (!servicesReportNoPagination || servicesReportNoPagination.length === 0) {
      return { totalPrice: 0, totalDiscount: 0, totalQuantity: 0 };
    }

    return servicesReportNoPagination.reduce(
      (acc, item: any) => ({
        totalPrice: acc.totalPrice + (item.price * item.quantity || 0),
        totalDiscount: acc.totalDiscount + (item.discount || 0),
        totalQuantity: acc.totalQuantity + (item.quantity || 0)
      }),
      { totalPrice: 0, totalDiscount: 0, totalQuantity: 0 }
    );
  }, [servicesReportNoPagination]);

  // Expose data to parent component for export
  useEffect(() => {
    if (servicesReportNoPagination && setDataSource) {
      setDataSource(servicesReportNoPagination);
    }
  }, [servicesReportNoPagination, setDataSource]);

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  return (
    <div className="service-report-container">
      <Table
        columns={columns}
        size="small"
        dataSource={servicesReport || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: page,
          position: ['bottomCenter'],
          total: pagination.total,
          pageSize: pageSize,
          locale: { items_per_page: `/ ${t('common.page')}` },
          showTotal: total => `${total} ${t('common.item')}`,
          onChange: (page: number) => handleChangePage(page),
          onShowSizeChange: (current, size) => {
            setPageSize(size);
            setPage(1);
          }
        }}
        locale={{
          emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
        }}
        scroll={{ x: 'max-content' }}
        bordered
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
              <Table.Summary.Cell index={0} colSpan={7} align="center">
                {t('common.total')}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                {formatCurrency(totals.totalPrice)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} align="right">
                {formatCurrency(totals.totalDiscount)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9} align="center">
                {totals.totalQuantity}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
}
